/**
 * Global Search Component
 *
 * Platform-wide search across all modules:
 * - Researchers
 * - Grants
 * - Publications
 * - Projects
 * - Partners
 */
import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  X,
  User,
  FileText,
  BookOpen,
  Building2,
  Briefcase,
  Clock,
  ArrowRight,
  Command,
  Loader2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

// Types
interface SearchResult {
  id: string
  type: 'researcher' | 'grant' | 'publication' | 'project' | 'partner'
  title: string
  subtitle: string
  highlight?: string
  url: string
  metadata?: Record<string, string>
}

interface SearchCategory {
  type: SearchResult['type']
  label: string
  icon: React.ComponentType<{ className?: string }>
  color: string
}

const CATEGORIES: SearchCategory[] = [
  { type: 'researcher', label: 'Researchers', icon: User, color: 'bg-blue-100 text-blue-700' },
  { type: 'grant', label: 'Grants', icon: FileText, color: 'bg-green-100 text-green-700' },
  { type: 'publication', label: 'Publications', icon: BookOpen, color: 'bg-purple-100 text-purple-700' },
  { type: 'project', label: 'Projects', icon: Briefcase, color: 'bg-orange-100 text-orange-700' },
  { type: 'partner', label: 'Partners', icon: Building2, color: 'bg-pink-100 text-pink-700' },
]

// Mock search results
const MOCK_RESULTS: SearchResult[] = [
  {
    id: 'r1',
    type: 'researcher',
    title: 'Dr. Maria Rossi',
    subtitle: 'Politecnico di Milano',
    highlight: 'AI & Machine Learning, Healthcare',
    url: '/cassiopeia/researchers?id=r1',
  },
  {
    id: 'r2',
    type: 'researcher',
    title: 'Prof. Hans Mueller',
    subtitle: 'Charité - Universitätsmedizin Berlin',
    highlight: 'Clinical Research, Oncology',
    url: '/cassiopeia/researchers?id=r2',
  },
  {
    id: 'g1',
    type: 'grant',
    title: 'Horizon Europe - AI for Healthcare',
    subtitle: 'HORIZON-HLTH-2024',
    highlight: 'Deadline: Sep 15, 2024 | €5M',
    url: '/sagitta/grants?id=g1',
  },
  {
    id: 'g2',
    type: 'grant',
    title: 'ERC Starting Grant 2024',
    subtitle: 'ERC-2024-STG',
    highlight: 'Deadline: Oct 15, 2024 | €1.5M',
    url: '/sagitta/grants?id=g2',
  },
  {
    id: 'p1',
    type: 'publication',
    title: 'Explainable Deep Learning for Medical Diagnosis',
    subtitle: 'Nature Machine Intelligence, 2023',
    highlight: '156 citations',
    url: '/pegasus/literature?id=p1',
  },
  {
    id: 'proj1',
    type: 'project',
    title: 'AI4Health: Interpretable AI for Clinical Decision Support',
    subtitle: 'ERC Starting Grant',
    highlight: 'Active | 2022-2027',
    url: '/cepheus/projects?id=proj1',
  },
  {
    id: 'partner1',
    type: 'partner',
    title: 'INRIA',
    subtitle: 'Research Institute, France',
    highlight: 'Computer Vision, Privacy-Preserving ML',
    url: '/andromeda/partners?id=partner1',
  },
]

interface GlobalSearchProps {
  className?: string
}

export function GlobalSearch({ className }: GlobalSearchProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [selectedCategory, setSelectedCategory] = useState<SearchResult['type'] | 'all'>('all')
  const [recentSearches, setRecentSearches] = useState<string[]>([
    'AI healthcare grants',
    'ERC Starting Grant',
    'Machine learning researchers',
  ])

  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  // Keyboard shortcut to open search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setIsOpen(true)
      }
      if (e.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  // Search logic
  useEffect(() => {
    if (!query.trim()) {
      setResults([])
      return
    }

    setIsLoading(true)
    // Simulate API call
    const timer = setTimeout(() => {
      const filtered = MOCK_RESULTS.filter(
        (r) =>
          (selectedCategory === 'all' || r.type === selectedCategory) &&
          (r.title.toLowerCase().includes(query.toLowerCase()) ||
            r.subtitle.toLowerCase().includes(query.toLowerCase()) ||
            r.highlight?.toLowerCase().includes(query.toLowerCase()))
      )
      setResults(filtered)
      setIsLoading(false)
      setSelectedIndex(0)
    }, 300)

    return () => clearTimeout(timer)
  }, [query, selectedCategory])

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex((prev) => Math.max(prev - 1, 0))
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      handleSelect(results[selectedIndex])
    }
  }

  const handleSelect = (result: SearchResult) => {
    // Add to recent searches
    if (!recentSearches.includes(query)) {
      setRecentSearches((prev) => [query, ...prev.slice(0, 4)])
    }
    setIsOpen(false)
    setQuery('')
    navigate(result.url)
  }

  const getCategoryInfo = (type: SearchResult['type']) => {
    return CATEGORIES.find((c) => c.type === type)!
  }

  const filteredResults =
    selectedCategory === 'all'
      ? results
      : results.filter((r) => r.type === selectedCategory)

  return (
    <>
      {/* Search Trigger */}
      <div className={cn('relative', className)}>
        <Button
          variant="outline"
          className="w-full md:w-80 justify-start text-muted-foreground"
          onClick={() => setIsOpen(true)}
        >
          <Search className="h-4 w-4 mr-2" />
          <span>Search researchers, grants, projects...</span>
          <kbd className="hidden md:inline-flex ml-auto items-center gap-1 rounded border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
            <Command className="h-3 w-3" />K
          </kbd>
        </Button>
      </div>

      {/* Search Modal */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />

          {/* Modal */}
          <div className="fixed inset-x-4 top-[10%] z-50 mx-auto max-w-2xl">
            <div className="rounded-xl border bg-background shadow-2xl">
              {/* Search Input */}
              <div className="flex items-center border-b px-4">
                <Search className="h-5 w-5 text-muted-foreground" />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Search across all modules..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="flex-1 bg-transparent px-4 py-4 text-sm outline-none placeholder:text-muted-foreground"
                />
                {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Category Filters */}
              <div className="flex items-center gap-1 px-4 py-2 border-b overflow-x-auto">
                <Button
                  variant={selectedCategory === 'all' ? 'default' : 'ghost'}
                  size="sm"
                  className="text-xs shrink-0"
                  onClick={() => setSelectedCategory('all')}
                >
                  All
                </Button>
                {CATEGORIES.map((cat) => (
                  <Button
                    key={cat.type}
                    variant={selectedCategory === cat.type ? 'default' : 'ghost'}
                    size="sm"
                    className="text-xs shrink-0 gap-1"
                    onClick={() => setSelectedCategory(cat.type)}
                  >
                    <cat.icon className="h-3 w-3" />
                    {cat.label}
                  </Button>
                ))}
              </div>

              {/* Results */}
              <div className="max-h-[50vh] overflow-y-auto">
                {query && filteredResults.length === 0 && !isLoading ? (
                  <div className="py-12 text-center">
                    <Search className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-sm text-muted-foreground">
                      No results found for "{query}"
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Try different keywords or filters
                    </p>
                  </div>
                ) : query ? (
                  <div className="py-2">
                    {filteredResults.map((result, index) => {
                      const category = getCategoryInfo(result.type)
                      const Icon = category.icon

                      return (
                        <button
                          key={result.id}
                          className={cn(
                            'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors',
                            selectedIndex === index
                              ? 'bg-accent'
                              : 'hover:bg-accent/50'
                          )}
                          onClick={() => handleSelect(result)}
                          onMouseEnter={() => setSelectedIndex(index)}
                        >
                          <div
                            className={cn(
                              'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
                              category.color
                            )}
                          >
                            <Icon className="h-5 w-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium truncate">{result.title}</p>
                              <Badge variant="outline" className="text-xs shrink-0">
                                {category.label}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground truncate">
                              {result.subtitle}
                            </p>
                            {result.highlight && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {result.highlight}
                              </p>
                            )}
                          </div>
                          <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                        </button>
                      )
                    })}
                  </div>
                ) : (
                  <div className="py-4">
                    {/* Recent Searches */}
                    <div className="px-4 pb-2">
                      <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Recent Searches
                      </p>
                      <div className="space-y-1">
                        {recentSearches.map((search, i) => (
                          <button
                            key={i}
                            className="w-full text-left px-2 py-1.5 text-sm hover:bg-accent rounded-md transition-colors"
                            onClick={() => setQuery(search)}
                          >
                            {search}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Quick Links */}
                    <div className="px-4 pt-2 border-t">
                      <p className="text-xs font-medium text-muted-foreground mb-2">
                        Quick Links
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="justify-start text-xs"
                          onClick={() => {
                            setIsOpen(false)
                            navigate('/sagitta/grants')
                          }}
                        >
                          <FileText className="h-3 w-3 mr-2" />
                          Browse Grants
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="justify-start text-xs"
                          onClick={() => {
                            setIsOpen(false)
                            navigate('/cassiopeia/search')
                          }}
                        >
                          <User className="h-3 w-3 mr-2" />
                          Find Researchers
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="justify-start text-xs"
                          onClick={() => {
                            setIsOpen(false)
                            navigate('/andromeda/partners')
                          }}
                        >
                          <Building2 className="h-3 w-3 mr-2" />
                          Partner Search
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="justify-start text-xs"
                          onClick={() => {
                            setIsOpen(false)
                            navigate('/pegasus/literature')
                          }}
                        >
                          <BookOpen className="h-3 w-3 mr-2" />
                          Literature
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between border-t px-4 py-2 text-xs text-muted-foreground">
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1">
                    <kbd className="rounded border px-1.5 py-0.5 bg-muted">↑</kbd>
                    <kbd className="rounded border px-1.5 py-0.5 bg-muted">↓</kbd>
                    to navigate
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="rounded border px-1.5 py-0.5 bg-muted">↵</kbd>
                    to select
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="rounded border px-1.5 py-0.5 bg-muted">esc</kbd>
                    to close
                  </span>
                </div>
                <span>Powered by ResearchConstellation</span>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}
